import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { REGIONS } from "@/lib/countries";

export default function Register() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { register } = useAuth();
  const [f, setF] = useState({
    role: "buyer", name: "", email: "", password: "", company_name: "", country: "", phone: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) { toast.error(t("auth.minChars")); return; }
    setBusy(true);
    const r = await register(f);
    setBusy(false);
    if (r.ok) {
      toast.success(t("auth.accountCreated"));
      nav("/dashboard");
    } else { toast.error(r.error); }
  };

  const googleSignIn = (role) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + `/dashboard?intent=${role}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const countryLabel = f.role === "supplier" ? `${t("auth.country")} (Asia)` : t("auth.country");

  return (
    <div data-testid="register-page" className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-2xl mx-auto px-6 py-12">
        <div className="overline text-primary mb-3">{t("auth.register").toUpperCase()}</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-secondary leading-tight">{t("auth.join")}</h1>
        <p className="mt-2 text-muted-foreground">{t("auth.reviewNote")}</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="register-form">
          <div className="md:col-span-2">
            <Label>{t("auth.iRegisterAs")}</Label>
            <RadioGroup value={f.role} onValueChange={(v) => setF({ ...f, role: v, country: "" })} className="grid grid-cols-2 gap-3 mt-2">
              <label className={`border rounded-md p-4 cursor-pointer ${f.role === "buyer" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="role-buyer">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="buyer" id="r-buyer" />
                  <div><div className="font-semibold text-secondary">{t("auth.buyerRole")}</div><div className="text-xs text-muted-foreground">{t("auth.buyerHint")}</div></div>
                </div>
              </label>
              <label className={`border rounded-md p-4 cursor-pointer ${f.role === "supplier" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="role-supplier">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="supplier" id="r-supplier" />
                  <div><div className="font-semibold text-secondary">{t("auth.supplierRole")}</div><div className="text-xs text-muted-foreground">{t("auth.supplierHint")}</div></div>
                </div>
              </label>
            </RadioGroup>
          </div>
          <div><Label htmlFor="r-name">{t("auth.fullName")}</Label><Input id="r-name" data-testid="register-name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} required/></div>
          <div><Label htmlFor="r-email">{t("auth.workEmail")}</Label><Input id="r-email" data-testid="register-email" type="email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} required/></div>
          <div><Label htmlFor="r-company">{t("auth.companyName")}</Label><Input id="r-company" data-testid="register-company" value={f.company_name} onChange={(e)=>setF({...f,company_name:e.target.value})}/></div>
          <div>
            <Label htmlFor="r-country">{countryLabel}</Label>
            {f.role === "supplier" ? (
              <select id="r-country" data-testid="register-country" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.country} onChange={(e)=>setF({...f,country:e.target.value})}>
                <option value="">— {t("auth.country")} —</option>
                {Object.entries(REGIONS).map(([region, countries]) => (
                  <optgroup key={region} label={region}>
                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                ))}
              </select>
            ) : (
              <Input id="r-country" data-testid="register-country" value={f.country} onChange={(e)=>setF({...f,country:e.target.value})}/>
            )}
          </div>
          <div><Label htmlFor="r-phone">{t("auth.phone")}</Label><Input id="r-phone" data-testid="register-phone" value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})}/></div>
          <div><Label htmlFor="r-pass">{t("auth.passwordMin")}</Label><Input id="r-pass" data-testid="register-password" type="password" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} required minLength={6}/></div>
          <div className="md:col-span-2 mt-2"><Button data-testid="register-submit" type="submit" disabled={busy} className="w-full h-11">{busy ? t("auth.creating") : t("auth.createAccount")}</Button></div>
        </form>

        <div className="relative my-8"><div className="border-t border-border" /><span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-background px-3 text-xs text-muted-foreground">{t("auth.or")}</span></div>
        <Button data-testid="google-register" onClick={() => googleSignIn(f.role)} variant="outline" className="w-full h-11">
          {t("auth.continueGoogleAs", { role: f.role === "supplier" ? t("auth.supplierRole") : t("auth.buyerRole") })}
        </Button>

        <p className="mt-8 text-sm text-center text-muted-foreground">{t("auth.haveAccount")} <Link to="/login" data-testid="go-login" className="text-primary font-medium hover:underline">{t("auth.goSignIn")}</Link></p>
      </section>
    </div>
  );
}
